import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  ReactCalendarGroupRendererProps,
  ItemRendererProps,
  TimelineGroup,
  TimelineItem,
} from './react-calendar-timeline';
import { useHotkeys } from 'react-hotkeys-hook';
import { TimelineStyle } from './timelineStyles';
import {
  Box,
  FilterBar,
  DeleteButton,
  Text,
  Button,
  DropdownButton,
  Menu,
  MenuItem,
} from '../ui';
import type {
  EventTimeline,
  TimelineFocus,
  TimelineVoyage,
} from '../core/Timelines';
import { getVoyages } from '../core/Timelines';
import last from 'lodash/last';
import { NONE, assertDefined, filteredMap, sortBy } from '../../lib/util';
import { TimelineVoyageRenderer } from './TimelineVoyage';
import type { ScenarioTimeline_OrganizationFragment } from '../../graphql';
import {
  FilterBarInput,
  FilterBarLocationSelect,
} from '../ui/FilterBar/FilterBar';
import type { LocationSelectValue } from '../LocationSelect';
import debounce from 'lodash/debounce';
import type { ScheduleDataContext } from '../core/ScheduleDataContext';
import type { CargoEvent, Leg, WaitEvent, TimeData } from '../core/TimeData';
import {
  getEventLocationId,
  isTimeData,
  isCargoEventTimeData,
} from '../core/TimeData';
import uniq from 'lodash/uniq';
import { useLocalSetting } from '../hooks/useLocalConfig';
import { getOrderDisplayName } from '../order/order';
import { scrollToToday } from './handlers';
import type { ITimelineView } from './TimelineView';
import TimelineView, { TimelineControlButtons } from './TimelineView';
import TimelinePreferencesContext from '../contexts/TimelinePreferencesContext';
import type { ScenarioOrder } from '../../lib/planningData';
import { NotFoundTimelineItems } from './NotFoundTimelineItems';
import type { GetItemPropsParams } from './react-calendar-timeline/lib/items/Item';
import { useIsMobileView } from '../hooks/useMediaQuery';
import TimelineResizeDetector from './TimelineResizeDetector';
import { VoyageMenuContext } from '../contexts/VoyageMenuContext';
import { useContextStore } from '../contexts/ZustandContext';

type TimelineWithVoyages = EventTimeline & {
  voyages: TimelineVoyage[];
  sortParams: {
    prevEnd: number | null; // End time of previous event
    ongoingOrNextStart: number | null; // Start time of currently ongoing event
  };
};
interface OperationsTimelineRef {
  scrollToToday: () => void;
}
interface OperationsTimelineProps {
  timelines: EventTimeline[];
  scheduleContext: Pick<ScheduleDataContext, 'locations'>;
  organizations: Map<number, ScenarioTimeline_OrganizationFragment>;
  sharedOrderIds: Set<number>;
  onViewAssignment: (timelineFocus?: TimelineFocus) => void;
}

type OperationsTimelineGroupFields = {
  title: string;
};

export type OperationsTimelineItem = TimelineItem<{
  orders: ScenarioOrder[];
  events: TimeData<CargoEvent>[];
  implicitEvents: Array<Leg | WaitEvent>;
  timelineId: EventTimeline['id'];
  voyage: TimelineVoyage;
  locations: OperationsTimelineProps['scheduleContext']['locations'];
  organizations: OperationsTimelineProps['organizations'];
  isShared: boolean;
  isCollapsed: true;
}>;

type OperationsTimelineGroup = TimelineGroup<OperationsTimelineGroupFields>;

const groupRenderer = ({
  group,
}: ReactCalendarGroupRendererProps<OperationsTimelineGroup>): React.JSX.Element => {
  return (
    <Box px={3}>
      <Text fontWeight="bold" isSingleLine>
        {group.title}
      </Text>
    </Box>
  );
};

const staticItemProps: GetItemPropsParams = {
  style: {
    background: undefined,
    border: undefined,
    borderRightWidth: undefined,
    borderWidth: `1px`,
    borderColor: '#c4cdda',
    borderStyle: 'solid',
    margin: `3px 0`,
  },
};
const itemRenderer = ({
  item,
  getItemProps,
  getResizeProps,
  itemContext: { dimensions, selected, pixelsPerMs },
}: ItemRendererProps<OperationsTimelineItem>) => {
  const itemProps = getItemProps(staticItemProps);

  itemProps.style.height = `${dimensions.height - 8}px`;

  const { locations, timelineId } = item;

  return (
    <div {...itemProps}>
      <TimelineVoyageRenderer
        item={item}
        selected={selected}
        locations={locations}
        timelineId={timelineId}
        pixelsPerMs={pixelsPerMs}
        zIndex={getResizeProps().left?.style.zIndex}
      />
    </div>
  );
};

type OperationsFilterValues = {
  locationId: number | undefined;
  search: string;
};

const Filters = ({
  searchKey,
  onScrollToToday,
  onChange,
  isMobileView,
}: {
  searchKey: string;
  onScrollToToday: () => void;
  onChange: (values: OperationsFilterValues) => void;
  isMobileView: boolean;
}) => {
  const [searchFilter, setSearchFilter] = useState<string>(searchKey ?? '');
  const [locationFilter, setLocationFilter] = useState<
    LocationSelectValue | undefined
  >(undefined);

  const { setZoomLevel } = useContext(TimelinePreferencesContext);

  const debouncedChange = useMemo(() => debounce(onChange, 400), [onChange]);

  useEffect(() => {
    debouncedChange({
      locationId: locationFilter?.value ?? undefined,
      search: searchFilter,
    });
  }, [debouncedChange, locationFilter, searchFilter]);

  useHotkeys(
    'meta+2, ctrl+2',
    () => {
      setZoomLevel(2);
    },
    { preventDefault: true },
  );
  useHotkeys(
    'meta+4, ctrl+4',
    () => {
      setZoomLevel(4);
    },
    { preventDefault: true },
  );

  const handleReset = () => {
    setSearchFilter('');
    setLocationFilter(undefined);
  };

  return (
    <FilterBar>
      <div />
      <Box display="flex">
        <Box display="flex" mx={3}>
          {!isMobileView && (
            <>
              <Button mx={3} onPress={() => onScrollToToday()}>
                Today
              </Button>
              <DropdownButton label="Zoom">
                <Menu hasShadow>
                  <MenuItem
                    label="Zoom for 2 weeks [CTRL + 2]"
                    onPress={() => setZoomLevel(2)}
                  />
                  <MenuItem
                    label="Zoom for 4 weeks [CTRL + 4]"
                    onPress={() => setZoomLevel(4)}
                  />
                </Menu>
              </DropdownButton>
            </>
          )}
          <TimelineControlButtons isOperationsMobileView={isMobileView} />
        </Box>

        <Box position="relative">
          <FilterBarInput
            name="search"
            label="Search"
            flexShrink={1}
            inputProps={{
              onChange: (e) => setSearchFilter(e.target.value),
              value: searchFilter,
              width: '150px',
            }}
            mr={3}
          />
          {!!searchFilter && (
            <DeleteButton
              position="absolute"
              right={12}
              bottom={2}
              marginBottom={1}
              onPress={() => handleReset()}
            />
          )}
        </Box>
        {!isMobileView && (
          <FilterBarLocationSelect
            label="Port"
            selectProps={{
              value: locationFilter,
              onChange: setLocationFilter,
            }}
            mr={3}
          />
        )}
      </Box>
    </FilterBar>
  );
};

const OperationsTimeline = forwardRef<
  OperationsTimelineRef,
  OperationsTimelineProps
>(
  (
    {
      onViewAssignment,
      timelines,
      scheduleContext,
      organizations,
      sharedOrderIds,
    },
    ref,
  ): React.JSX.Element => {
    const [timelineKeyword, setTimelineKeyword] = useLocalSetting(
      'timelineKeyword',
      '',
    );
    const [filters, setFilters] = useState<OperationsFilterValues>({
      locationId: undefined,
      search: timelineKeyword,
    });

    const showEmptyTimelines = false;

    const timelinesWithVoyages = useMemo<TimelineWithVoyages[]>(() => {
      const tls: TimelineWithVoyages[] = [];
      const now = new Date().getTime();

      timelines.forEach((tl) => {
        const voyages = getVoyages(tl).filter((voyage) => {
          const locationFilterMatch =
            typeof filters.locationId === 'number'
              ? voyage.cargoEvents
                  .map((e) => e.event.locationId)
                  .includes(filters.locationId)
              : true;

          const searchFilterMatch =
            filters.search.length > 0
              ? voyage.cargoEvents
                  .flatMap(({ event }) => [
                    event.order.locationByLoadPortId?.name ?? '',
                    event.order.locationByDischargePortId?.name ?? '',
                    event.vessel?.name ?? '',
                    event.order.assortmentByAssortmentId?.name ?? '',
                    getOrderDisplayName(event.order),
                    event.order.id.toString() ?? '',
                    event.order.shipmentId?.toString() ?? '',
                    event.order.cargoAmount.toString(),
                    event.order.cargoUnitByCargoUnitId?.name ?? '',
                  ])
                  .join(' ')
                  .toLowerCase()
                  .includes(filters.search)
              : true;

          return locationFilterMatch && searchFilterMatch;
        });

        if (voyages.length > 0 || showEmptyTimelines) {
          // Store some parameters here to avoid iterating through the events in the sorting fn
          const cargoEvents = voyages.flatMap((voyage) =>
            filters.locationId !== null
              ? voyage.cargoEvents.filter(
                  ({ event }) => event.locationId === filters.locationId,
                )
              : voyage.cargoEvents,
          );

          let prevEnd: number | null = null;
          let ongoingOrNextStart: number | null = null;

          for (const event of cargoEvents) {
            if (event.end < now && (prevEnd === null || event.end > prevEnd)) {
              prevEnd = event.end;
            }

            if (
              (event.arriveInPort < now && event.end > now) ||
              event.arriveInPort > now
            ) {
              ongoingOrNextStart = event.arriveInPort;
              break;
            }
          }

          tls.push({
            ...tl,
            voyages,
            sortParams: {
              prevEnd,
              ongoingOrNextStart,
            },
          });
        }
      });

      tls.sort((tlA, tlB) => {
        if (
          // Sort by currently ongoing event or next ETA if available
          tlA.sortParams.ongoingOrNextStart !== null ||
          tlB.sortParams.ongoingOrNextStart !== null
        ) {
          const aStart = tlA.sortParams.ongoingOrNextStart;
          const bStart = tlB.sortParams.ongoingOrNextStart;

          return bStart === null || (aStart !== null && aStart <= bStart)
            ? -1
            : 1;
        } else if (
          tlA.sortParams.prevEnd !== null ||
          tlB.sortParams.prevEnd !== null
        ) {
          // If ETA not available for either timeline then sort by latest departure
          const aEnd = tlA.sortParams.prevEnd;
          const bEnd = tlB.sortParams.prevEnd;

          return bEnd === null || (aEnd !== null && aEnd >= bEnd) ? -1 : 1;
        }

        return 0;
      });

      return tls;
    }, [filters, showEmptyTimelines, timelines]);

    const getVesselTimelineId = (
      vessel: NonNullable<EventTimeline['vessel']>,
    ) => `vessel-${vessel.id}`;

    const groups = useMemo<OperationsTimelineGroup[]>(() => {
      const vesselsHandled = new Set<number>();
      return filteredMap(timelinesWithVoyages, (timeline) => {
        if (!timeline.vessel) {
          return {
            id: timeline.id,
            title: timeline.title,
          };
        }

        if (vesselsHandled.has(timeline.vessel.id)) {
          return NONE;
        }
        vesselsHandled.add(timeline.vessel.id);
        return {
          id: getVesselTimelineId(timeline.vessel),
          title: timeline.vessel.name ?? timeline.title,
        };
      });
    }, [timelinesWithVoyages]);

    const items = useMemo<OperationsTimelineItem[]>(
      () =>
        timelinesWithVoyages.flatMap((timeline) => {
          let prevItem: OperationsTimelineItem | null = null;
          return timeline.voyages.map((voyage) => {
            const prevItemEvents =
              prevItem && 'events' in prevItem
                ? prevItem.events.filter(isCargoEventTimeData)
                : [];

            const orderEventsWithGlobals = sortBy(
              voyage.cargoEvents
                .filter(isTimeData)
                .filter(isCargoEventTimeData),
              (x) => x.start,
            );

            const prevItemLocation = getEventLocationId(
              prevItemEvents[prevItemEvents.length - 1]?.event || {},
            );
            const currentItemLocation = getEventLocationId(
              orderEventsWithGlobals[0]?.event || {},
            );

            const firstEvent = voyage.cargoEvents[0];
            const firstStart =
              prevItem?.group === timeline.id &&
              prevItemLocation === currentItemLocation
                ? orderEventsWithGlobals[0]?.start
                : (firstEvent.previousEventStart ?? firstEvent.start);
            const lastEnd = last(voyage.cargoEvents)?.end;
            const orders = uniq(voyage.cargoEvents.map((e) => e.event.order));
            const orderIds = orders.map((o) => o.id);
            assertDefined(lastEnd, `expected an end timestamp for a voyage`);
            const item: OperationsTimelineItem = {
              id: `${orderIds.join('-')}`,
              group: timeline.vessel
                ? getVesselTimelineId(timeline.vessel)
                : timeline.id,
              start_time: firstStart,
              end_time: lastEnd,
              events: voyage.cargoEvents ?? [],
              orders,
              implicitEvents: voyage.implicitEvents,
              timelineId: timeline.id,
              voyage,
              locations: scheduleContext.locations,
              organizations,
              isShared: orders.every((o) => sharedOrderIds.has(o.id)),
              isCollapsed: true,
            };
            prevItem = item;
            return item;
          });
        }),
      [
        organizations,
        scheduleContext.locations,
        sharedOrderIds,
        timelinesWithVoyages,
      ],
    );

    const handleViewItemDetails = useCallback(
      (itemId: OperationsTimelineItem['id'], event: React.SyntheticEvent) => {
        const item = items.find((item) => item.id === itemId);
        const cellData = (event.target as HTMLElement).textContent || '';

        if (item && 'orders' in item) {
          onViewAssignment({
            timelineId: item.timelineId,
            ...(item.voyage.orderId !== null
              ? { orderIds: [item.voyage.orderId] }
              : { shipmentIds: [item.voyage.shipment.clientId] }),
            navTarget: {
              tabName: 'documents',
              portLocation: cellData,
            },
          });
        }
      },
      [onViewAssignment, items],
    );

    const timelineRef =
      useRef<ITimelineView<OperationsTimelineItem, OperationsTimelineGroup>>(
        null,
      );

    useImperativeHandle(ref, () => ({
      scrollToToday: () => scrollToToday(timelineRef),
    }));

    const onChangeHandler = useCallback(
      (values: OperationsFilterValues) => {
        setTimelineKeyword(values.search ?? '');
        setFilters(values);
      },
      [setTimelineKeyword],
    );

    const isMobileView = useIsMobileView();

    return (
      <>
        <Filters
          searchKey={filters.search}
          onScrollToToday={() => scrollToToday(timelineRef)}
          onChange={onChangeHandler}
          isMobileView={isMobileView}
        />
        <Box display="flex" flexDirection="column" flex={1}>
          <TimelineStyle />
          <div
            style={{
              position: 'relative',
              width: '100%',
              height: '100%',
              overflowY: 'auto',
            }}
          >
            {!groups.length && <NotFoundTimelineItems />}
            <VoyageMenuContext.Provider value={useContextStore({})}>
              <TimelineView<OperationsTimelineItem, OperationsTimelineGroup>
                timelineRef={timelineRef}
                groups={groups}
                items={items}
                selected={[]}
                groupRenderer={groupRenderer}
                resizeDetector={TimelineResizeDetector}
                itemRenderer={itemRenderer}
                onItemDoubleClick={handleViewItemDetails}
                onItemTap={handleViewItemDetails}
                canMove={false}
                headerHeight={57}
                sidebarWidth={isMobileView ? 140 : 200}
                stackItems={true}
              ></TimelineView>
            </VoyageMenuContext.Provider>
          </div>
        </Box>
      </>
    );
  },
);

export default OperationsTimeline;
